/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { AdErrorModal } from './AdErrorModal';
import { AdSpendConfirmModal } from './AdSpendConfirmModal';
import type { AdErrorInfo, AdSpendSummary } from './ad-dialog-types';

export interface AdDialogs {
  /** Resolves true to proceed, false when the user cancels. Every spend is confirmed: there is no opt-out. */
  confirmSpend: (summary: AdSpendSummary) => Promise<boolean>;
  /** Opens the "why it failed / what to do" dialog. */
  showAdError: (error: AdErrorInfo) => void;
  /** Render this once in the owning component. */
  dialogs: ReactNode;
}

/**
 * Promise-style access to the two ad dialogs, so a call site only needs
 * `if (!(await confirmSpend(...))) return;` and `showAdError(...)`.
 *
 * @returns The two openers and the node that renders them.
 */
export function useAdDialogs(): AdDialogs {
  const [pendingSpend, setPendingSpend] = useState<AdSpendSummary | null>(null);
  const [adError, setAdError] = useState<AdErrorInfo | null>(null);
  const resolveSpend = useRef<((accepted: boolean) => void) | null>(null);

  const confirmSpend = useCallback((summary: AdSpendSummary): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolveSpend.current = resolve;
      setPendingSpend(summary);
    });
  }, []);

  const settleSpend = useCallback((accepted: boolean) => {
    resolveSpend.current?.(accepted);
    resolveSpend.current = null;
    setPendingSpend(null);
  }, []);

  const closeError = useCallback(() => {
    adError?.onClose?.();
    setAdError(null);
  }, [adError]);

  const dialogs = (
    <>
      {pendingSpend && (
        <AdSpendConfirmModal
          summary={pendingSpend}
          onConfirm={() => settleSpend(true)}
          onCancel={() => settleSpend(false)}
        />
      )}
      {adError && <AdErrorModal error={adError} onClose={closeError} />}
    </>
  );

  return { confirmSpend, showAdError: setAdError, dialogs };
}
